import { Button, Form, Input, Checkbox } from "antd";
import { setAuthData, setRememberMe } from "../../utils/auth";
import { IconUser, IconLock, IconView, IconHide } from '../../shared/components/icons';
import { login } from "../../core/api/services/authService";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation, Trans } from "react-i18next";
import { executeRecaptcha, preloadRecaptcha } from "../../core/security/recaptcha";
import { useBranding } from "../../core/hooks/useBranding";
import AuthScene from "./AuthScene";
import AuthMessage from "./AuthMessage";
import "./auth.css";

const RATE_LIMIT_FALLBACK_SECONDS = 30;

const Login = () => {
  const [loading, setLoading] = useState(false);
  const [retryAfterSeconds, setRetryAfterSeconds] = useState(0);
  // A failed sign-in stays on the panel as a row that can be re-read, and marks
  // both fields — the server never says which was wrong, so the UI must not
  // imply it knows. Cleared on the next keystroke.
  const [authError, setAuthError] = useState(false);
  const { t } = useTranslation();
  const { loginSubtitle, selfServiceResetEnabled } = useBranding();

  useEffect(() => {
    preloadRecaptcha().catch(() => {});
  }, []);

  useEffect(() => {
    if (retryAfterSeconds <= 0) return undefined;
    const timerId = window.setInterval(() => {
      setRetryAfterSeconds((prev) => Math.max(prev - 1, 0));
    }, 1000);
    return () => window.clearInterval(timerId);
  }, [retryAfterSeconds]);

  const clearError = () => {
    if (authError) setAuthError(false);
  };

  const onFinish = async (values) => {
    if (retryAfterSeconds > 0) return;

    try {
      setLoading(true);
      setAuthError(false);
      const recaptchaToken = await executeRecaptcha("login");
      const response = await login({
        username: values.username,
        password: values.password,
        recaptchaToken,
      });
      if (response.data.success) {
        setRememberMe(values.remember || false);
        setAuthData(response.data.data, values.remember || false);
        window.location.reload();
      } else {
        setAuthError(true);
      }
    } catch (err) {
      if (err?.statusCode === 429) {
        const cooldownSeconds = err?.retryAfterSeconds || RATE_LIMIT_FALLBACK_SECONDS;
        setRetryAfterSeconds((prev) => Math.max(prev, cooldownSeconds));
        return;
      }
      setAuthError(true);
    } finally {
      setLoading(false);
    }
  };

  const rateLimited = retryAfterSeconds > 0;

  return (
    <AuthScene
      panelTitle={t("auth.login")}
      line={loginSubtitle || t("auth.loginSubtitle")}
    >
      <Form
        name="login-form"
        onFinish={onFinish}
        onValuesChange={clearError}
        autoComplete="off"
        layout="vertical"
        requiredMark={false}
        className="lms-auth__form"
      >
        <Form.Item
          label={t("auth.username")}
          name="username"
          rules={[
            { required: true, message: t("validation.required") },
            { type: "string", message: t("validation.required") },
          ]}
        >
          {/*
            No placeholder: the label above the control already names it, and a
            placeholder saying the same thing vanishes the moment you type — the
            phone's field dropped it for that reason, and this is the same field.
          */}
          <Input
            prefix={<IconUser />}
            size="large"
            autoComplete="username"
            status={authError ? "error" : ""}
          />
        </Form.Item>

        <Form.Item
          label={t("auth.password")}
          name="password"
          rules={[{ required: true, message: t("validation.required") }]}
          style={{ marginBottom: 0 }}
        >
          <Input.Password
            prefix={<IconLock />}
            // The glyph is where a tap goes — an eye while the password is hidden —
            // and it is the registry's Lucide pair, not antd's own, so the toggle is
            // the same glyph the phone shows.
            iconRender={(visible) => (visible ? <IconHide /> : <IconView />)}
            size="large"
            autoComplete="current-password"
            status={authError ? "error" : ""}
          />
        </Form.Item>

        <div className="lms-auth__row">
          <Form.Item name="remember" valuePropName="checked" initialValue={true} noStyle>
            <Checkbox>{t("auth.rememberMe")}</Checkbox>
          </Form.Item>
          {/*
            Only shown where self-service reset actually exists. On a normal
            deployment there is no SMS gateway, most students have no phone, and
            the reset happens at the desk — so the link is replaced by the
            instruction that leads somewhere.
          */}
          {selfServiceResetEnabled ? (
            <Link to="/forgot-password" className="lms-auth__link">
              {t("auth.forgotPassword")}
            </Link>
          ) : (
            <span className="lms-auth__hint">{t("auth.forgotPasswordAskAdmin")}</span>
          )}
        </div>

        {authError && (
          <AuthMessage variant="error" title={t("auth.loginFailed")}>
            {t("auth.loginErrorHint")}
          </AuthMessage>
        )}

        {rateLimited && (
          <AuthMessage variant="warn" title={t("auth.tooManyAttempts")}>
            <Trans
              i18nKey="auth.rateLimitRetryAfterFig"
              values={{ seconds: retryAfterSeconds }}
              components={{ fig: <span className="lms-auth__fig" /> }}
            />
          </AuthMessage>
        )}

        <Form.Item style={{ marginBottom: 0 }}>
          <Button
            type="primary"
            htmlType="submit"
            loading={loading}
            disabled={rateLimited}
            block
            size="large"
            className="lms-auth__submit"
          >
            {loading ? t("common.loading") : t("auth.login")}
          </Button>
        </Form.Item>
      </Form>
    </AuthScene>
  );
};

export default Login;
